import { createRef, useEffect } from 'react'
import { Box } from '@mui/material'
import { useFormikContext, useField } from 'formik'
import { useTranslation } from 'react-i18next'

import DropDownMenu from 'src/components/shared/DropDownMenu/DropDownMenu'
import { fieldsetTableStyles } from 'src/components/shared/styles/FieldsetTableStyles'
import InfoBar from 'src/components/shared/ui/InfoBar/InfoBar'
import { TootltipInfoType } from 'src/components/shared/ui/TooltipInfo'
import { SwitchField } from 'src/components/shared/ui/form/fields'
import InputFileButton from 'src/components/shared/ui/form/fields/InputFileButton'
import {
  excesoCabidaFileName,
  excesoCabidaSubTipoObject
} from 'src/components/views/EscritosLexnet/components/shared/components/Adjuntos/components/const'
import DocumentoPrincipalTable from 'src/components/views/EscritosLexnet/components/shared/components/Adjuntos/components/tables/DocumentoPrincipalTable'
import { LexonDocuments } from 'src/components/views/EscritosLexnet/components/shared/components/LexonDocuments'
import { EscritoFormInitialValues } from 'src/components/views/EscritosLexnet/components/shared/types/escritosFormTypes'
import { useTipoMensajeContext } from 'src/contexts/TipoMensajeContext'
import { FormField } from 'src/enums/Form'
import { Orden } from 'src/interfaces/Catalog'
import { DocumentoPrincipalType, SubTipoDocumento } from 'src/interfaces/EscritosLegales'
import { LexonDocumentsRefType } from '../../LexonDocuments/components/LexonDocuments'
import { documentoPrincipalStyles as styles } from './DocumentoPrincipalStyles'
import { useEscritosFormContext } from '../../SubmitForm'

function DocumentoPrincipal(): JSX.Element {
  const { t } = useTranslation()

  const tooltipInfo: TootltipInfoType = {
    title: t('form.field.documento_principal_pendiente_completar_tooltip'),
    placement: 'right'
  }

  const [documentoPrincipalArchivo] = useField(FormField.DOCUMENTO_PRINCIPAL_ARCHIVO)

  const { errors, setFieldValue, validateField } = useFormikContext()
  const { validateAfterSubmit } = useEscritosFormContext()
  const { isMonitorio } = useTipoMensajeContext()

  const [orden] = useField<Orden>(
    `${FormField.ORGANO_DESTINO}.${FormField.ORGANO_DESTINO_ORDEN_JUDICIAL}`
  )

  const refLexonDocuments = createRef<LexonDocumentsRefType>()

  useEffect(() => {
    if (documentoPrincipalArchivo.value.file?.name) {
      const documentoPrincipal: DocumentoPrincipalType = {
        archivo: documentoPrincipalArchivo.value?.file?.name || null,
        subtipo: null,
        descripcion: '',
        pendienteCompletar: false,
        hash: documentoPrincipalArchivo.value?.hash || null,
        sign: true
      }
      if (
        documentoPrincipalArchivo.value.file.name &&
        documentoPrincipalArchivo.value.file.name.startsWith(excesoCabidaFileName)
      ) {
        setFieldValue(FormField.EXCESO_CABIDA, false)
        documentoPrincipal.subtipo = excesoCabidaSubTipoObject as SubTipoDocumento
        documentoPrincipal.pendienteCompletar = true
      }
      setFieldValue(FormField.DOCUMENTO_PRINCIPAL, [documentoPrincipal])

      validateAfterSubmit &&
        setTimeout(() => {
          validateField(FormField.DOCUMENTO_PRINCIPAL)
        })
    } else {
      setFieldValue(
        FormField.DOCUMENTO_PRINCIPAL,
        EscritoFormInitialValues[FormField.DOCUMENTO_PRINCIPAL]
      )
    }
  }, [documentoPrincipalArchivo.value, validateField, setFieldValue])

  return (
    <Box sx={styles.wrapper}>
      {isMonitorio && (
        <Box sx={{ marginBottom: '16px' }}>
          <InfoBar
            message={t('alert.documento_principal_autogenerado')}
            iconVariant="info"
            showCloseIcon={false}
          />
        </Box>
      )}
      {!isMonitorio && (
        <Box sx={fieldsetTableStyles.wrapper}>
          <Box
            sx={fieldsetTableStyles.actions}
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
          >
            <Box component="span" flex="1 1 auto" sx={fieldsetTableStyles.actionsInfo}>
              {t('form.legend.documento_principal')}
            </Box>
            <DropDownMenu
              id="documentoPrincipal"
              title={t('form.button.add')}
              items={[
                {
                  title: t('form.button.addNew'),
                  onClick: () => {
                    document.getElementById(FormField.DOCUMENTO_PRINCIPAL_ARCHIVO)?.click()
                  },
                  disabled: !orden.value
                },
                {
                  title: t('form.button.addFromLexon'),
                  onClick: () => {
                    refLexonDocuments?.current?.open()
                  },
                  disabled: !orden.value
                }
              ]}
            />
          </Box>
          <DocumentoPrincipalTable id={FormField.DOCUMENTO_PRINCIPAL} />
        </Box>
      )}
      {errors[FormField.DOCUMENTO_PRINCIPAL] && (
        <Box component="span" sx={styles.requiredField}>
          {t('form.validation.required.required_table')}
        </Box>
      )}
      <SwitchField
        id={`${FormField.DOCUMENTO_PRINCIPAL}.0.pendienteCompletar`}
        label={t('form.field.documento_principal_pendiente_completar')}
        tooltipInfo={tooltipInfo}
        disabled={documentoPrincipalArchivo.value.hash === null && !isMonitorio}
        sx={styles.switch}
      />
      {!isMonitorio && (
        <>
          <div style={{ display: 'none' }}>
            <InputFileButton id={FormField.DOCUMENTO_PRINCIPAL_ARCHIVO} disabled={!orden.value} />
          </div>
          <LexonDocuments
            id={FormField.DOCUMENTO_PRINCIPAL_ARCHIVO}
            ref={refLexonDocuments}
            disabled={!orden.value}
          />
        </>
      )}
    </Box>
  )
}

export default DocumentoPrincipal
